import { Fragment, useEffect, useMemo, useState } from "react";
import {
    useNavigate,
    useParams,
} from "react-router-dom";

import { getListPage } from "../services/ads/ads.service";
import { links } from "../constants/links";

import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

import { styled } from "@mui/material/styles";
import { createSvgIcon } from "@mui/material/utils";

import styles from "./List.module.scss";


type LinkProp = {
    url: string,
    label: string,
};

type SEListPage = {
    id: number,
    footer_links: LinkProp[],
    header_image_file?: string,
    logo_image_file?: string,
    highlight_section_title?: string,
};

type SEListPageItem = {
    banner_image?: string,
    banner_image_file: string,
    banner_image_link?: string,
    buttons: LinkProp[],
    descriptions: string[],
    id: number,
    is_support_highlight_section: boolean,
    reward_text: string,
    subcampaign_id: number,
    title: string,
    sequence?: number,
};

type CarouselItemProps = {
    href?: string,
    fileName?: string,
    imageSource: string,
};

/**
 * Width of the container is 94% of screen width for all screen size.
 *
 * Height of image is 140px.
 *
 * Aspect ratio is 1280 x 720
 *
 * Column Gap is 8px
 *
 * Calculate how many items to show on a screen width breakpoint for
 * display full image without cut off (maintain aspect ratio),
 * all image same height 100% of 140px.
 *
 * Formula:
 * 140 / 720 * 1280 = 248.89px (width)
 *
 * n = number of items
 * t = container width
 * s = screen width / breakpoint
 *
 * t = n * 140 / 720 * 1280 + (n - 1) * 8
 *
 * s = t / 94%
 *
 * 3 items = 811.34751773
 *
 */
const carouselToScreen = 0.94;
const calcBreakpoint = (itemsCount: number): number => (itemsCount * 140 / 720 * 1280 + (itemsCount - 1) * 8) / carouselToScreen;

const responsive = {
  superWideDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: 3000, min: calcBreakpoint(7) },
    items: 7,
    partialVisibilityGutter: 0,
  },
  extraWideDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: calcBreakpoint(7) - 1, min: calcBreakpoint(6) },
    items: 6,
    partialVisibilityGutter: 0,
  },
  wideDesktop: {
    // the naming can be any, depends on you.
    breakpoint: { max: calcBreakpoint(6) - 1, min: calcBreakpoint(5) },
    items: 5,
    partialVisibilityGutter: 0,
  },
  desktop: {
    breakpoint: { max: calcBreakpoint(5) - 1, min: calcBreakpoint(4) },
    items: 4,
    partialVisibilityGutter: 0,
  },
  tablet: {
    breakpoint: { max: calcBreakpoint(4) - 1, min: calcBreakpoint(3) },
    items: 3,
    partialVisibilityGutter: 0,
  },
  mobile: {
    breakpoint: { max: calcBreakpoint(3) - 1, min: 0 },
    items: 1,
    partialVisibilityGutter: 0,
  }
};

const CarouselWrapper = styled("div")({
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
});

const CarouselItemWrapper = styled("div")({
    width: "100%",
    height: 140,
    overflow: "hidden",

    "& img": {
        width: "auto",
        height: "100%",
        objectFit: "contain", /* fills div, may crop */
    },

    [`@media (min-width: ${responsive.mobile.breakpoint.min}px) and (max-width: ${responsive.mobile.breakpoint.max}px)`]: {
        maxWidth: `${responsive.mobile.breakpoint.max * carouselToScreen}px`,
        height: "auto",

        "& img": {
            width: "100%"
        }
    },
});

const CaretRightIcon = createSvgIcon(
    // viewBox="0 0 9 12
    <path d="M9 6L1.41795e-06 11.1962L1.87221e-06 0.803847L9 6Z" />,
    "Caret Right"
);

function CarouselItem({ href, fileName, imageSource }: CarouselItemProps) {
    const imageElement = <img alt={fileName} src={imageSource} />;

    return (
        <CarouselItemWrapper>
            {
                href
                    ? (
                        <a
                            href={href}
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            {imageElement}
                        </a>
                    )
                    : imageElement
            }
        </CarouselItemWrapper>
    );
}

function Item(props: SEListPageItem) {
    const {
        title,
        reward_text: rewardText,
        banner_image: imageAlt,
        banner_image_file: imageSource,
        banner_image_link: imageLink,
        descriptions,
        buttons,
    } = props;

    const imageElement = (
        <img
            alt={imageAlt}
            src={imageSource}
            style={{
                width: "100%",
                height: "auto",
                borderRadius: 15,
                boxShadow: "6px 6px 9px rgba(0, 0, 0, 0.2)",
                objectFit: "cover",
            }}
        />
    );

    return (
        <li style={{ width: "100%" }}>
            <div
                style={{
                    display: "flex",
                    flexWrap: "nowrap",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: 620,
                    margin: "0 auto",
                }}
            >
                <div
                    style={{
                        fontSize: 14,
                        display: "flex",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        columnGap: 5,
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "hidden",
                    }}
                >
                    <CaretRightIcon
                        fontSize="inherit"
                        htmlColor="#000"
                        viewBox="0 0 9 12"
                    />
                    <span
                        style={{
                            fontSize: "inherit",
                            fontWeight: 700,
                            textDecoration: "underline",
                            maxWidth: "100%",
                            overflowX: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}>
                        {title}
                    </span>
                </div>
                <p
                    style={{
                        flex: "0 0 auto",
                        color: "#961e37",
                        fontSize: 16,
                        fontWeight: 700,
                    }}
                >
                    {rewardText}
                </p>
            </div>
            <figure
                style={{
                    width: "100%",
                    height: "auto",
                    maxWidth: 620,
                    margin: "10px auto",
                }}
            >
                {
                    imageLink
                        ? (
                            <a
                                href={imageLink}
                                target="_blank"
                                rel="noreferrer noopener"
                            >
                                {imageElement}
                            </a>
                        )
                        : imageElement
                }
            </figure>
            {
                Array.isArray(descriptions) && descriptions.length && descriptions.some(Boolean)
                    ? (
                        <p style={{ fontSize: "1.125rem", margin: "12px 0", padding: 0, textAlign: "center" }}>
                            <strong>
                                {
                                    descriptions.map(
                                        (line, index) => <Fragment key={index}>{line}<br /></Fragment>
                                    )
                                }
                            </strong>
                        </p>
                    )
                    : null
            }
            {
                Array.isArray(buttons) && buttons.length
                    ? (
                        <div
                            style={{
                                margin: "0 auto",
                                padding: 0,
                                display: "grid",
                                gridTemplateColumns: "1fr",
                                gap: 30,
                                width: "max-content",
                                minWidth: 330,
                                maxWidth: "100%",
                            }}
                        >
                            {
                                buttons.map(
                                    ({ url, label }, index) => (
                                        <a
                                            key={index}
                                            className={styles.button}
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            style={{
                                                color: "#fff",
                                                fontSize: "1.125rem",
                                                textAlign: "center",
                                                borderRadius: 50,
                                                padding: "3px 2.5em",
                                                background: "linear-gradient(to right, #00a1e9, #00a1e9)",
                                                textShadow: "0 0 black",
                                                boxShadow: "0px 15px 17px -14px #535050"
                                            }}
                                        >
                                            {label}
                                        </a>
                                    )
                                )
                            }
                        </div>
                    )
                    : null
            }
            <hr style={{ marginTop: 30, marginBottom: 40, maxWidth: 620 }} />
        </li>
    );
}

export function List(this: any) {
    const { id }: any = useParams();

    const navigate = useNavigate();

    const [notFound, setNotFound] = useState(false);

    const [listPage, setListPage] = useState<SEListPage>({
        id: 0,
        footer_links: [],
        header_image_file: "",
        logo_image_file: "",
        highlight_section_title: "",
    });
    const [items, setItems] = useState<SEListPageItem[]>([]);

    const highlightItems = useMemo(
        () => items.filter(
            ({ is_support_highlight_section, banner_image_file }: SEListPageItem) => is_support_highlight_section && banner_image_file
        ),
        [items]
    );

    const [isDataFetched, setIsDataFetched] = useState(false);

    useEffect(
        () => {
            if (id && !isNaN(parseInt(id))) {
                getListPage(parseInt(id))
                    .then(
                        response => {
                            if (response.status === 200 && response.data) {
                                setNotFound(false); // Not necessary but add a second line of defense.

                                const { se_list_page, se_list_page_items } = response.data;
                                setListPage(se_list_page);

                                se_list_page_items.sort(
                                    (a: SEListPageItem, b: SEListPageItem) => {
                                        if (a.sequence == null && b.sequence == null) return 0;
                                        if (a.sequence == null) return 1;
                                        if (b.sequence == null) return -1;
                                        return a.sequence - b.sequence;
                                    }
                                );

                                setItems(se_list_page_items);

                                setIsDataFetched(true);
                            } else {
                                navigate(`${links.SMART_RETAIL_ERROR}?error-msg=campaign_error`);
                            }
                        }
                    )
                    .catch(
                        error => {
                            if (error.response?.status === 404) {
                                setNotFound(true);
                            }
                            navigate(`${links.SMART_RETAIL_ERROR}?error-msg=campaign_error`);
                        }
                    );
            } else {
                navigate(`${links.SMART_RETAIL_ERROR}?error-msg=campaign_error`);
            }
        },
        []
    );

    if (notFound) {
        return <div>Not Found!</div>;
    } else if (!isDataFetched) {
        return <div>Loading...</div>;
    }

    const style: React.CSSProperties & { [key: string]: any } = {
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen-Sans, Ubuntu, Cantarell, 'Helvetica Neue', sans-serif",
        fontSize: "1.125rem",
        lineHeight: 1.6,
        fontWeight: 400,
        fontStyle: "normal",
        ["-webkit-font-smoothing"]: "antialiased",
        padding: "24px 0 48px",
        width: "94%",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        rowGap: 32,
    };

    return (
        <div style={style}>
            {
                listPage.logo_image_file
                    ? (
                        <img
                            alt="logo"
                            decoding="async"
                            loading="lazy"
                            src={listPage.logo_image_file}
                            style={{ maxHeight: 80, width: "auto", maxWidth: "100%" }}
                        />
                    )
                    : null
            }
            {
                listPage.header_image_file
                    ? (
                        <img
                            alt="header-image"
                            decoding="async"
                            loading="lazy"
                            src={listPage.header_image_file}
                            style={{ height: "auto", width: "auto", maxHeight: 280, maxWidth: "100%" }}
                        />
                    )
                    : null
            }
            {
                highlightItems.length
                    ? (
                        <CarouselWrapper>
                            {
                                listPage.highlight_section_title
                                    ? (
                                        <p style={{ textAlign: "center", marginBottom: 20 }}>
                                            <strong>
                                                {listPage.highlight_section_title}
                                            </strong>
                                        </p>
                                    )
                                    : null
                            }
                            <Carousel
                                autoPlay
                                shouldResetAutoplay
                                rewind
                                rewindWithAnimation

                                responsive={responsive}

                                draggable={false}
                                swipeable

                                arrows={false}

                                showDots
                                renderDotsOutside
                                dotListClass={styles["carousel-dot-list"]}
                            >
                                {
                                    highlightItems.map(
                                        ({ banner_image, banner_image_file, banner_image_link }: SEListPageItem, index) =>
                                            <CarouselItem
                                                key={index}
                                                href={banner_image_link}
                                                fileName={banner_image}
                                                imageSource={banner_image_file}
                                            />
                                    )
                                }
                            </Carousel>
                        </CarouselWrapper>
                    )
                    : null
            }
            {
                items.length
                    ? (
                        <ul
                            style={{
                                listStyle: "none",
                                margin: 0,
                                padding: 0,
                                width: "100%",
                            }}
                        >
                            {
                                items.map(
                                    (item, index) => <Item key={index} {...item} />
                                )
                            }
                        </ul>
                    )
                    : null
            }
            {
                Array.isArray(listPage.footer_links) && listPage.footer_links.length
                    ? (
                        <div>
                            {
                                listPage.footer_links.map(
                                    ({ url, label }, index) => (
                                        <a
                                            key={index}
                                            href={url}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            style={{ display: "block", textAlign: "center", color: "#333", textDecoration: "underline", textUnderlineOffset: "0.15em" }}
                                        >
                                            {label}
                                        </a>
                                    )
                                )
                            }
                        </div>
                    )
                    : null
            }
        </div>
    );
}
